import React, { useEffect, useCallback, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import { BsSearch } from 'react-icons/bs';
import * as _ from 'lodash';
import { ERROR_CODE_REPORT, DOWNLOAD_ERROR_CODE_REPORT, DOWNLOAD_PDF_ERROR_CODE_REPORT } from 'actions/dataReport';
import { useSelector, useDispatch } from 'react-redux';
import SearchBox from 'components/general/SearchBox';
import moment from 'moment-timezone';
import AdminHeader from 'components/header';
import LayoutContainer from 'components/layout/LayoutContainer';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import { BsDownload } from 'react-icons/bs';
import DateTimePicker from 'components/inputs/DateTimePicker';
import { BiSort } from 'react-icons/bi';
import { IoSearchOutline } from 'react-icons/io5';
import fileDownload from 'js-file-download';
import ReactPagination from 'components/general/ReactPagination';
import Select from 'components/inputs/Select';

// CR13 - CPMS Dashboard Phase 6, Milestone 3: Error Code Report.
// Lists all transient OCPP error codes raised by chargers, with the linked Transaction ID
// when the error occurred during an active charging session. Server-paginated + sortable.
const ErrorCodeReport = () => {
  const dispatch = useDispatch();
  const [sortByItem, setSortByItem] = useState({ item: '', order: '' });
  const [searchText, setSearchText] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [startDate, setStartDate] = useState(moment().tz('Asia/Dubai').subtract(1, 'day').startOf('day'));
  const [endDate, setEndDate] = useState(moment(new Date()).tz('Asia/Dubai').subtract(1, 'day').endOf('day'));
  const [selectedOption, setSelectedOption] = useState('excel');
  const reportFormats = [
    { label: 'PDF Format', value: 'pdf' },
    { label: 'Excel Sheet', value: 'excel' },
  ];

  const errorCodeReportList = useSelector((state) => state?.errorCodeReport?.errorCodeReport);
  const isLoader = useSelector((state) => state?.errorCodeReport?.isLoading);
  const page = useSelector((state) => state?.errorCodeReport?.page);
  const limit = useSelector((state) => state?.errorCodeReport?.limit);
  const totalData = useSelector((state) => state?.errorCodeReport?.totalResults);
  const totalPages = useSelector((state) => state?.errorCodeReport?.totalPages);
  const summary = useSelector((state) => state?.errorCodeReport?.summary);
  const profileData = useSelector((state) => state.profile.userProfile);
  const userProfileStations = profileData?.charging_stations || [];
  const isLoading = useSelector((state) => state.downloadingLoader.isLoading);
  const hasData = errorCodeReportList && errorCodeReportList.length > 0;

  const errorCodeReport = useCallback(
    (value) => {
      const base = {
        ...value,
        from: moment(startDate).tz('Asia/Dubai').utc(),
        to: moment(endDate).tz('Asia/Dubai').utc(),
      };
      if (searchText) {
        base.search = searchText;
      }
      if (_.isEmpty(value?.charging_station) && !_.isEmpty(userProfileStations)) {
        base.charging_station = userProfileStations;
      }
      dispatch({
        type: ERROR_CODE_REPORT,
        payload: base,
      });
    },
    [startDate, endDate, searchText]
  );

  const searchHandler = (event) => {
    setSearchText(event.target.value);
  };

  const handleSearch = () => {
    errorCodeReport({});
  };

  useEffect(() => {
    errorCodeReport();
  }, []);

  const downloadErrorCodeReport = useCallback(() => {
    const data = {
      from: moment(startDate).tz('Asia/Dubai').utc(),
      to: moment(endDate).tz('Asia/Dubai').utc(),
      reportFormat: selectedOption,
    };
    if (searchText) {
      data.search = searchText;
    }
    if (!_.isEmpty(userProfileStations)) {
      data.charging_station = userProfileStations;
    }
    dispatch({
      type: selectedOption === 'excel' ? DOWNLOAD_ERROR_CODE_REPORT : DOWNLOAD_PDF_ERROR_CODE_REPORT,
      payload: data,
      cb: (res) => {
        if (res && _.get(res, 'status') === 200) {
          fileDownload(res.data, `Error Code Report.${selectedOption === 'excel' ? 'xlsx' : 'pdf'}`);
        }
      },
    });
  }, [startDate, endDate, selectedOption, searchText]);

  const downloadFile = () => {
    downloadErrorCodeReport();
  };

  const getSortByItem = useCallback(
    (name, order, pageData) => {
      const sortedOrder = order || 'asc';
      const data = {
        sortBy: `${sortedOrder === 'asc' ? '' : '-'}${name}`,
        charging_station: !_.isEmpty(userProfileStations) ? userProfileStations : '',
      };
      if (pageData) {
        data.page = pageData.selected + 1;
      }
      errorCodeReport(data);
    },
    [errorCodeReport]
  );

  const handleSorting = (sortItem, dataItem) => {
    if (dataItem.item === sortItem) {
      const sortOrder = dataItem.order === 'asc' ? 'desc' : 'asc';
      setSortByItem({ item: sortItem, order: sortOrder });
      getSortByItem(sortItem, sortOrder);
    } else {
      setSortByItem({ item: sortItem, order: 'asc' });
      getSortByItem(sortItem, 'asc');
    }
  };

  const handlePageClick = (pageVal) => {
    if (sortByItem.item && sortByItem.order) {
      getSortByItem(sortByItem.item, sortByItem.order, pageVal);
    } else {
      errorCodeReport({ page: pageVal.selected + 1 });
    }
  };

  const search = (
    <SearchBox
      preIcon={<BsSearch />}
      value={searchText}
      onChange={searchHandler}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          handleSearch();
        }
      }}
    />
  );
  const searchIcon = <IoSearchOutline size={27} onClick={() => setShowSearchBar(true)} className="report-search-icon" />;

  return (
    <>
      <LayoutContainer>
        <AdminHeader title="Error Code Report" />
        <div className="page-content-wrapper scrollable">
          <div className="data-report--main">
            <div className="data-report--block">
              <Card>
                <div className="data-report__inner">
                  <div className="data-report__box">
                    <Row className="data-report__row">
                      <Col
                        xl={`${showSearchBar ? 'auto' : 'auto'}`}
                        md={`${showSearchBar ? 'auto' : 'auto'}`}
                        className={`${showSearchBar ? '' : 'data-report-search-box'}`}
                      >
                        {showSearchBar ? search : searchIcon}
                      </Col>
                      <Col xl={`${showSearchBar ? 'auto' : 'auto'}`} md={`${showSearchBar ? 'auto' : 'auto'}`}>
                        <div className="data-report-date_picker">
                          <DateTimePicker
                            onChangeOfStartDate={(item) => setStartDate(item)}
                            onChangeOfEndDate={(item) => setEndDate(item)}
                            timeFormatOfStartDate={false}
                            timeFormatOfEndDate={false}
                            initialValueOfStartDate={moment(new Date()).tz('Asia/Dubai').subtract(1, 'day').startOf('day')}
                            initialValueOfEndDate={moment(new Date()).tz('Asia/Dubai').subtract(1, 'day').endOf('day')}
                          />
                        </div>
                      </Col>
                      <Col xl={'auto'} md={'auto'}>
                        <div className="report-search-box" onClick={() => errorCodeReport({})}>
                          <Button className="report-search-button">Search</Button>
                        </div>
                      </Col>
                      <Col xl={2} md={2}>
                        <Select
                          options={reportFormats}
                          value={selectedOption}
                          onChange={(value) => setSelectedOption(value)}
                          placeholder="Select Report Format"
                        />
                      </Col>
                      <Col xl={'auto'} md={'auto'} className="excel-icon--block" onClick={hasData ? downloadFile : null}>
                        {isLoading ? (
                          <div className="loader--block">
                            <Spinner as="span" animation="border" size="sm" role="status" />
                          </div>
                        ) : (
                          <div className={`report-excel-icon  ${!hasData ? 'disabled' : ''}`}>
                            <BsDownload title={!hasData ? '' : 'Download'} size={28} color={!hasData ? '#d3d3d3' : '#3c7cdd'} />
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>
                  {!_.isEmpty(summary) && (
                    <div className="data-report__table" style={{ marginBottom: '1rem' }}>
                      <div className="summary-title" style={{ fontWeight: 600, padding: '8px 4px' }}>
                        Top {summary.length} Chargers by Error Count
                      </div>
                      <div className="table-responsive">
                        <table className="record-list-table">
                          <thead>
                            <tr>
                              <th><div className="sorting"><span>Sr No.</span></div></th>
                              <th><div className="sorting"><span>Charger ID</span></div></th>
                              <th><div className="sorting"><span>Station Name</span></div></th>
                              <th><div className="sorting"><span>Station Code</span></div></th>
                              <th><div className="sorting"><span>Error Count</span></div></th>
                              <th><div className="sorting"><span>Most Frequent Error</span></div></th>
                              <th><div className="sorting"><span>Count</span></div></th>
                            </tr>
                          </thead>
                          <tbody>
                            {_.map(summary, (row, key) => (
                              <tr key={`error-summary-${key}`}>
                                <td>{key + 1}</td>
                                <td>{_.get(row, 'ocpp_id', '-') || '-'}</td>
                                <td>{_.get(row, 'station_name', '-') || '-'}</td>
                                <td>{_.get(row, 'station_code', '-') || '-'}</td>
                                <td>{_.get(row, 'error_count', 0)}</td>
                                <td>{_.get(row, 'top_error_code', '-') || '-'}</td>
                                <td>{_.get(row, 'top_error_code_count', 0)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  <div className="data-report__table">
                    <div className="table-responsive">
                      <table className="record-list-table" id="table-to-xls">
                        <thead>
                          <tr>
                            <th>
                              <div className="sorting">
                                <span>Sr No.</span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>Station Name</span>
                                <span className="ico" onClick={() => handleSorting('station_name', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>Station Code</span>
                                <span className="ico" onClick={() => handleSorting('station_code', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>Charger ID</span>
                                <span className="ico" onClick={() => handleSorting('ocpp_id', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>Connector ID</span>
                                <span className="ico" onClick={() => handleSorting('connector_id', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>Error Code</span>
                                <span className="ico" onClick={() => handleSorting('error_code', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>Description</span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>Info</span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>Vendor Error Code</span>
                                <span className="ico" onClick={() => handleSorting('vendor_error_code', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>Status</span>
                                <span className="ico" onClick={() => handleSorting('charger_status', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>Timestamp</span>
                                <span className="ico" onClick={() => handleSorting('timestamp', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>Transaction ID</span>
                                <span className="ico" onClick={() => handleSorting('transaction_id', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoader ? (
                            <tr>
                              <td colSpan={12} className="border-0">
                                <div className="loader--block">
                                  <Spinner as="span" animation="border" size="sm" role="status" />
                                  <span className="ps-2">Loading...</span>
                                </div>
                              </td>
                            </tr>
                          ) : _.isEmpty(errorCodeReportList) ? (
                            <tr>
                              <td colSpan={12} className="border-0">
                                <div className="empty-data-block">No Error Code Report Found</div>
                                <div className="text-center" style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>
                                  Note: error-code capture is forward-only — events are recorded from the feature go-live
                                  date onward, so date ranges before then will show no data.
                                </div>
                              </td>
                            </tr>
                          ) : (
                            !(isLoader || _.isUndefined(isLoader)) &&
                            _.map(errorCodeReportList, (item, key) => {
                              const serial_num = limit * (page - 1) + key;
                              return (
                                <tr key={`error-code-report-${serial_num}`}>
                                  <td>{serial_num + 1}</td>
                                  <td>{_.get(item, 'station_name', '-')}</td>
                                  <td>{_.get(item, 'station_code', '-')}</td>
                                  <td>{_.get(item, 'ocpp_id', '-')}</td>
                                  <td>{_.get(item, 'connector_id', '-')}</td>
                                  <td>{_.get(item, 'error_code', '-')}</td>
                                  <td>{_.get(item, 'description') ? _.get(item, 'description') : '-'}</td>
                                  <td>{_.get(item, 'info') ? _.get(item, 'info') : '-'}</td>
                                  <td>{_.get(item, 'vendor_error_code') ? _.get(item, 'vendor_error_code') : '-'}</td>
                                  <td>{_.get(item, 'charger_status', '-')}</td>
                                  <td>{_.get(item, 'timestamp_formatted', '-')}</td>
                                  <td>{_.get(item, 'transaction_id') ? _.get(item, 'transaction_id') : '-'}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {!(isLoader || _.isUndefined(isLoader)) && !_.isEmpty(errorCodeReportList) && (
                    <ReactPagination
                      currentPage={page}
                      limit={limit}
                      total={totalData}
                      handlePageClick={(pageVal) => handlePageClick(pageVal)}
                      totalPages={totalPages}
                      marginPagesDisplayed={1}
                    />
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </LayoutContainer>
    </>
  );
};

export default ErrorCodeReport;
